/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import { Box, debounce, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Popup from "../components/popups/popup";
import SearchInput from "../components/inputs/searchInput";
import UserDropdown from "../components/dropdowns/userDropdown";
import TimeDropdown from "../components/dropdowns/timeDropdown";
import OnOffSwitchForDeleted from "../components/inputs/onOffSwitchForDeleted";
import { formatPrice } from "../utils";
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import WalletTransferService from "../services/walletTransferService";
import UpdateTransferBox from "../components/boxes/walletTransfer/updateTransferBox";
import AddTransferBox from "../components/boxes/walletTransfer/addTransferBox";
import ViewTransferModal from "../components/modals/walletTransfer/viewTransferModal";
import DeleteTransferModal from "../components/modals/walletTransfer/deleteTransferModal";
import TransferTypeDropdown from "../components/dropdowns/transferTypeDropdown";
import PdfWalletTransfer from "../components/prints/pdfWalletTransfer";
import BranchDropdown from "../components/dropdowns/branchDropdown";
import React from "react";
import { AuthContext } from "../provider/authProvider";

const WalletTransfer = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [transfers, setTrasnfers] = useState([]);
    const [selectedTransfer, setSelectedTransfer] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedTime, setSelectedTime] = useState({
        time: 'today',
        value: t('today')
    });
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalDeposit, setTotalDeposit] = useState(0);
    const [totalWithdraw, setTotalWithdraw] = useState(0);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);
    const [checkFirstRender, setCheckFirstRender] = useState(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (transfer) => {
        setSelectedTransfer(transfer);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);

    const handleReload = async () => {
        setOpenUpdate(false);
        setHasMore(true);
        setPage(1);
        setSearchTerm('');
        setSelectedUser(null);
        setSelectedType(null);
        await fetchTransfers();

    };

    const handleOpenUpdate = (transfer) => {
        setOpenUpdate(true);
        setSelectedTransfer(transfer);
    };

    const handleOpenDelete = (transfer) => {
        setSelectedTransfer(transfer);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            setSelectedUser(null);
            setSelectedType(null);
            await fetchTransfers();
        }
    };

    const debouncedFetchBranches = useMemo(() =>
        debounce((name, userId, type, selectedBranch) => {
            setPage(1);
            setHasMore(true);
            fetchTransfers(1, name, userId, type, selectedBranch);
        }, 1000),
        []
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchBranches(value, selectedUser?.id, selectedType, selectedBranch?.id);
    };

    const handleType = (type) => {
        if (type === t("deposit")) return "deposit";
        else if (type === t("withdraw")) return "withdraw";
    };

    const fetchTransfers = async (page = 1, name = '', userId = null, type = null, branchId = null) => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                userId, branchId, name, deleted,
                type: handleType(type),
                ...selectedTime,
                ...sortState
            };
            const data = await WalletTransferService.fetch(page, filters);
            const { transaction, totalDeposit, totalWithdraw } = data;
            setTotalDeposit(totalDeposit);
            setTotalWithdraw(totalWithdraw);

            if (transaction.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setTrasnfers([]);
                }
            } else {
                setTrasnfers((prevs) => (page === 1 ? transaction : [...prevs, ...transaction]));
            }
        } catch (error) {
            console.error('Error fetching transaction:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetFilter = () => {
        setSearchTerm('');
        setSelectedUser(null);
        setHasMore(true);
        setSelectedType(null);
        setSelectedTime({
            time: 'today',
            value: t('today')
        });
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchTransfers(page, searchTerm, selectedUser?.id, selectedType, selectedBranch?.id);
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            isFirstRender.current = true;
            setPage(1);
        }
        fetchTransfers(1, searchTerm, selectedUser?.id, selectedType, checkFirstRender ? null : selectedBranch?.id);
        setCheckFirstRender(false);
    }, [selectedUser, selectedType, sortState, deleted, selectedTime, selectedBranch]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchTransfers(page + 1, searchTerm, selectedUser?.id, selectedType, selectedBranch?.id);
                setPage(prevPage => prevPage + 1);
            }
        }
    };


    return (
        <Box sx={{
            p: isTablet ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteTransferModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} transfer={selectedTransfer} />
                <ViewTransferModal open={openView} handleClose={handleCloseView} transfer={selectedTransfer} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {isTablet && <Box>
                {
                    (selectedTransfer && openUpdate) ?
                        <UpdateTransferBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} transfer={selectedTransfer} />
                        :
                        <AddTransferBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} />
                }
            </Box>}

            {/* Filter row */}
            {
                isTablet ?
                    <Box
                        sx={{
                            mb: 2,
                            mt: 1,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            width: '100%'
                        }}
                    >

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50,
                        }}>
                            <Box sx={{ px: 2, }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={'90%'}>
                                <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                            </Box>
                        </Box>

                        {allBranchPermission && <Box>
                            <Divider />
                            <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                                <BranchDropdown
                                    isFilter={true}
                                    selectedBranch={selectedBranch}
                                    setSelectedBranch={setSelectedBranch}
                                />
                            </Box>
                        </Box>}

                        <Divider />
                        <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                            <UserDropdown
                                isFilter={true}
                                selectedUser={selectedUser}
                                setSelectedUser={setSelectedUser}
                            />
                        </Box>

                        <Divider />
                        <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                            <TransferTypeDropdown
                                isFilter={true}
                                selectedType={selectedType}
                                setSelectedType={setSelectedType}
                            />
                        </Box>

                        <Divider />

                        <Box sx={{ alignContent: 'center', height: 50, width: '100%' }}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                        <Divider />


                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50
                        }}>
                            <Box width={'50%'}>
                                <Typography variant="body2" sx={{ mx: 0.5 }}>
                                    {t('totalDeposit')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalDeposit)}</span>
                                </Typography>
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={'50%'}>
                                <Typography variant="body2" sx={{ mx: 0.5 }}>
                                    {t('totalWithdraw')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalWithdraw)}</span>
                                </Typography>
                            </Box>
                        </Box>
                        <Divider />


                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50
                        }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: '20%'
                                }}
                            >
                                <PdfWalletTransfer
                                    data={{
                                        user: selectedUser,
                                        branch: selectedBranch,
                                        type: handleType(selectedType),
                                        name: searchTerm,
                                        time: selectedTime,
                                        transfers: transfers,
                                        deleted: deleted,
                                    }}
                                />
                            </Box>
                            <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                            <OnOffSwitchForDeleted
                                checkDeleted={deleted}
                                setCheckDeleted={setDeleted} />
                            <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>

                    </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            mt: 1,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Box sx={{ px: 2, }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" sx={{ height: '101px' }} />
                        <Box sx={{
                            width: '100%'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 50,
                            }}>
                                <Box width={allBranchPermission ? '25%' : '35%'}>
                                    <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                                </Box>
                                <Divider orientation="vertical" />
                                <Box width={allBranchPermission ? '25%' : '35%'}>
                                    <UserDropdown
                                        isFilter={true}
                                        selectedUser={selectedUser}
                                        setSelectedUser={setSelectedUser}
                                    />
                                </Box>
                                {allBranchPermission && <Divider orientation="vertical" />}
                                {allBranchPermission && <Box width={'25%'}>
                                    <BranchDropdown
                                        isFilter={true}
                                        selectedBranch={selectedBranch}
                                        setSelectedBranch={setSelectedBranch}
                                    />
                                </Box>}
                                <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                                <OnOffSwitchForDeleted
                                    checkDeleted={deleted}
                                    setCheckDeleted={setDeleted} />
                                <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        color: theme.palette.text.red,
                                        px: 2,
                                        cursor: 'pointer',
                                    }}
                                    onClick={resetFilter}
                                >
                                    <ReplayOutlinedIcon />
                                    <Box width={3}></Box>
                                    <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                                </Box>
                            </Box>

                            <Divider />
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 50
                            }}>

                                <Box width={'40%'}>
                                    <TimeDropdown
                                        isFilter={true}
                                        selectedTime={selectedTime}
                                        setSelectedTime={setSelectedTime}
                                    />
                                </Box>

                                <Divider orientation="vertical" />
                                <Box width={'14%'}>
                                    <TransferTypeDropdown
                                        isFilter={true}
                                        selectedType={selectedType}
                                        setSelectedType={setSelectedType}
                                    />
                                </Box>
                                <Divider orientation="vertical" />

                                <Box width={'20%'}>
                                    <Typography variant="body1" sx={{ mx: 1 }}>
                                        {t('totalDeposit')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalDeposit)}</span>
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" />
                                <Box width={'20%'}>
                                    <Typography variant="body1" sx={{ mx: 1 }}>
                                        {t('totalWithdraw')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalWithdraw)}</span>
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: '6%'
                                    }}
                                >
                                    <PdfWalletTransfer
                                        data={{
                                            user: selectedUser,
                                            branch: selectedBranch,
                                            type: handleType(selectedType),
                                            name: searchTerm,
                                            time: selectedTime,
                                            transfers: transfers,
                                            deleted: deleted,
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
            }

            <Box sx={{
                display: 'flex'
            }}>

                {!isTablet && <Box sx={{
                    width: '35%'
                }}>
                    {
                        (selectedTransfer && openUpdate) ?
                            <UpdateTransferBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} transfer={selectedTransfer} />
                            :
                            <AddTransferBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} />
                    }
                </Box>}

                {!isTablet && <Box width={10}></Box>}

                {/* Scrollable Table with Sticky Header */}
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        boxShadow: 'none',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        borderRadius: 2,
                        height: 'calc(100vh - 220px)',
                        overflowY: 'auto',
                        width: isTablet ? '100%' : '65%'
                    }}
                >
                    <Table stickyHeader aria-label="simple table">
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <TableRow>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('id')}
                                        {sortState.sortBy === 'id' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('user')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('user')}
                                        {sortState.sortBy === 'user' && sortIcon()}
                                    </Box></TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('branch')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('branch')}
                                        {sortState.sortBy === 'branch' && sortIcon()}
                                    </Box></TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('type')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('type')}
                                        {sortState.sortBy === 'type' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('amount')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('amount')}
                                        {sortState.sortBy === 'amount' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('date')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('date')}
                                        {sortState.sortBy === 'date' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transfers.map((transfer, index) => (
                                <TableRow
                                    key={transfer.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{transfer.id}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{transfer.user.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{transfer.branch.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box sx={{
                                            fontWeight: 'bold',
                                            color: transfer.type === 'deposit' ? theme.palette.green.box : theme.palette.red.box,
                                        }}>
                                            {t(transfer.type)}</Box>
                                    </TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(transfer.amount)}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{transfer.date}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            border: 1,
                                            borderColor: theme.palette.customGrey.borderInput,
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: 1,
                                        }}>

                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.7,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenView(transfer)}>
                                                <RemoveRedEyeOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>

                                            <Divider orientation="vertical" flexItem />

                                            {transfer.deletedAt === null && <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenUpdate(transfer)}>
                                                <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>}
                                            {transfer.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenDelete(transfer)}>
                                                {transfer.deletedAt === null ?
                                                    <DeleteOutlineOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.red.bg }} />
                                                    :
                                                    <ReplayOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.green.bg }} />
                                                }
                                            </Box>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>


        </Box>
    );
}

export default WalletTransfer;